import { OrderResource, OrderField } from "@/models/api/orders/get";
import permissions from "@/security/permissions";
import { formatDate, formatBool, formatPhoneNumber, formatAddress } from "@/utils";
import { IOrderStore, IOrderResource } from "@/store/types";

const defaultOrder: IOrderResource = {
  actualDeliveryDate: null,
  actualReceiveDate: null,
  anticipatedReceiveDate: null,
  billTo: {
    id: null,
    name: null,
    address: { streetLine1: null, streetLine2: null, city: null, state: null, zip: null },
    primaryPhone: null,
    secondaryPhone: null,
    location: null,
    standardizedAddress: null,
    agentId: null,
    deliveryForId: 0,
    displayId: null,
    addAsConsigneeContact: false
  },
  bookedBy: null,
  cabinetCount: 0,
  carrier: null,
  codPending: false,
  addressVerificationStatusId: null,
  consignee: {
    name: "",
    id: "",
    address: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      zip: ""
    },
    primaryPhone: "",
    secondaryPhone: "",
    location: null,
    standardizedAddress: null,
    agentId: null,
    deliveryForId: 0,
    displayId: null,
    addAsConsigneeContact: false
  },
  contactCompanyId: null,
  crn: null,
  cubes: 0,
  customerNotReady: false,
  datePlaced: null,
  deliveryType: null,
  dockSearchDate: null,
  driverNote: null,
  facility: null,
  followUpDate: null,
  hasNotes: false,
  hasShipsWithOrders: false,
  headerNumber: 0,
  intermediateConsignee: null,
  isScheduled: false,
  loadDate: null,
  manufacturer: null,
  miles: 0,
  number: null,
  trackingNumber: null,
  trackingNumberUrl: null,
  orderBy: {
    name: "",
    id: "",
    address: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      zip: ""
    },
    primaryPhone: "",
    secondaryPhone: "",
    location: null,
    standardizedAddress: null,
    agentId: null,
    deliveryForId: 0,
    displayId: null,
    addAsConsigneeContact: false
  },
  orderType: null,
  rateBy: null,
  received: false,
  remarks: "",
  requestedDeliveryDate: null,
  scheduledDeliveryDate: null,
  shipDate: null,
  shipper: {
    name: "",
    id: "",
    address: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      zip: "",
    },
    primaryPhone: "",
    secondaryPhone: "",
    location: null,
    standardizedAddress: null,
    agentId: null,
    deliveryForId: 0,
    displayId: null,
    addAsConsigneeContact: false
  },
  specialRequests: null,
  status: null,
  timeframeEnd: null,
  timeframeStart: null,
  truckId: null,
  truckName: null,
  unitCount: 0,
  copyOfOrder: false,
  isAsapDelivery: false,
  payPeriod: null,
  hasPOD: false,
  isPortalAccessible: false,
  barcodeOrderId: null,
  hasShipmentNotice: false,
  inventoryLinkable: false,
  deliveryForId: 0,
  deliveryForName: "CDS Logistics",
  agentManufacturer: null,
  isOnHold: false
};

const defaultOrderFields: Array<OrderField> = [


  { key: "deliveryForAgentName", label: "Delivery For", sortable: false, hidden: false, width:"10px", sort: 1, disableMoving:true, showInTooltip:true, padding: "0px" },
  { key: "headerNumber", label: "SYS #", sortable: true, hidden: false },
  {
    key: "crn",
    label: "CRN",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.crn
  },
  {
    key: "po",
    label: "PO",
    sortable: true,
    hidden: false
  },
  { key: "truckName", label: "Truck Name", sortable: true, hidden: false },
  { key: "truckStatus", label: "Truck Status", sortable: true, hidden: false },
  {
    key: "consignee.name",
    label: "Name",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  {
    key: "consignee.address.streetLine1",
    label: "Address",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  {
    key: "consignee.address.streetLine2",
    label: "Address 2",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  {
    key: "consignee.address.city",
    label: "City",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  {
    key: "consignee.address.zip",
    label: "Zip",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  {
    key: "consignee.address.state",
    label: "State",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.consignee
  },
  { key: "unitCount", label: "Unit Count", sortable: true, hidden: false },
  { key: "cubes", label: "Cubes", sortable: true, hidden: false },
  { key: "weight", label: "Weight", sortable: true, hidden: false },
  {
    key: "shipDate",
    label: "Ship Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  {
    key: "anticipatedReceiveDate",
    label: "Est. Recv Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  {
    key: "actualReceiveDate",
    label: "Act. Recv Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  {
    key: "requestedDeliveryDate",
    label: "Req Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  {
    key: "scheduledDeliveryDate",
    label: "Sched Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  { key: "timeframe", sortable: false, label: "Timeframe", hidden: false },
  {
    key: "actualDeliveryDate",
    label: "Act Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false,
    requiresPermission: permissions.order.view.dates
  },
  {
    key: "facility",
    label: "Facility",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.facility
  },
  {
    key: "remarks",
    label: "Remarks",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.remark
  },
  {
    key: "specialRequests",
    label: "Special Req",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.specialRequest
  },
  {
    key: "manufacturer",
    label: "Manufacturer",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.manufacturer
  },
  {
    key: "deliveryType",
    label: "Del Type",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.deliveryType
  },
  {
    key: "billTo.displayId",
    label: "Bill To",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.billTo
  },
  {
    key: "carrier",
    label: "Carrier",
    sortable: true,
    hidden: false,
    requiresPermission: permissions.order.view.carrier
  },
  { key: "Shipments", label: "Shipments", sortable: true, hidden: false },
  { key: "status", label: "Status", sortable: true, hidden: false },
  { key: "miles", label: "Miles", sortable: true, hidden: false },
  { key: "bookedBy", label: "Booked By", sortable: true, hidden: false },
  {
    key: "isAsapDelivery",
    label: "Wants ASAP",
    sortable: false,
    formatter: formatBool,
    hidden: false
  },
  {
    key: "shipper.name",
    label: "Shipper Name",
    sortable: false,
    hidden: false
  },

  {
    key: "shipper.primaryPhone",
    label: "Shipper Number",
    sortable: false,
    formatter: formatPhoneNumber,
    hidden: false
  },
  {
    key: "shipper.address",
    label: "Shipper Address",
    sortable: false,
    formatter: formatAddress,
    hidden: false
  },
  { key: "received", label: "Received", sortable: false, formatter: formatBool, hidden: false },
  { key: "customerNotReady", label: "Cust Not Ready", sortable: false, formatter: formatBool, hidden: false }
];
const name: string = "order";
const cdsFacilities: Array<string> = ["BALT","READ","TAMCDS","ORLCDS","GRCDS","PGHCDS","NPTCDS"];

export { defaultOrder, defaultOrderFields, name, cdsFacilities };
